import {
  Badge,
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import React from 'react';

import {
  formatYen,
  RELATION_KIND_STYLE,
  RelationKind,
} from '@/pages/externals/pos-menu-masters/service';
import {
  MosPosAccessory,
  MosPosComponent,
  MosPosMenu,
  MosPosPricePeriod,
} from '@/services/MosPosMenuMaster';

type Props = {
  menu: MosPosMenu;
  businessDate: string;
};

type ChildItem = Pick<
  MosPosComponent,
  | 'mets_menu_cd'
  | 'mets_screen_menu_name'
  | 'prices'
  | 'quantity'
  | 'display_order'
  | 'sell_price_difference'
  | 'reduced_sell_price_difference'
  | 'sell_quantity'
  | 'mets_crust_cd'
  | 'mets_size_cd'
>;

const tableBadgeStyle: React.CSSProperties = {
  padding: '2px 8px',
};

/** 種別タグ。色は凡例と共有し、見た目がずれないようにします。 */
const kindBadgeStyle = (kind: RelationKind): React.CSSProperties => ({
  ...tableBadgeStyle,
  ...RELATION_KIND_STYLE[kind],
});

const relationGroupStyle: React.CSSProperties = {
  background: 'white',
  border: '1px solid #E2E8F0',
  borderRadius: '5px',
  overflow: 'hidden',
};

const relationGroupHeadStyle: React.CSSProperties = {
  background: 'white',
  borderBottom: '1px solid #E2E8F0',
  fontSize: '12px',
  gap: '10px',
  padding: '9px 14px',
};

const muted = (value = '—') => (
  <Text as="span" color="gray.400" fontFamily="mono">
    {value}
  </Text>
);

/** 表示基準日に有効な単価。無ければ先頭期間を確認用に出します。 */
const priceAt = (
  prices: MosPosPricePeriod[],
  businessDate: string
): MosPosPricePeriod | undefined => {
  const covering = prices.filter(
    (price) =>
      price.sell_start_date <= businessDate &&
      businessDate <= price.sell_end_date
  );
  return (
    covering.find((price) => price.sell_stop_flag === 0) ??
    covering[0] ??
    prices[0]
  );
};

const priceText = (prices: MosPosPricePeriod[], businessDate: string) => {
  const price = priceAt(prices, businessDate);
  if (!price) return muted();
  return (
    <>
      <Text as="span" fontFamily="mono">
        {formatYen(price.sell_price)}
      </Text>{' '}
      <Text as="span" color="gray.500" fontFamily="mono">
        / {formatYen(price.reduced_sell_price)}
      </Text>
    </>
  );
};

/** 価格差額は値が指定されたチョイスだけに表示します。 */
const priceDifference = (item: ChildItem) => {
  if (
    item.sell_price_difference === undefined &&
    item.reduced_sell_price_difference === undefined
  ) {
    return muted();
  }
  const normal = item.sell_price_difference ?? 0;
  const reduced = item.reduced_sell_price_difference ?? 0;
  if (normal === 0 && reduced === 0) return muted('±0円');
  const sign = (value: number) => (value >= 0 ? `+${value}` : `${value}`);
  return (
    <Text as="span" color="#1D4ED8" fontFamily="mono" fontWeight="bold">
      {sign(normal)}円 / {sign(reduced)}円
    </Text>
  );
};

/** 数量・生地・サイズはチョイス価格に対する適用条件として表示します。 */
const priceCondition = (item: ChildItem) => {
  const parts: string[] = [];
  if (item.sell_quantity !== undefined) parts.push(`数量${item.sell_quantity}`);
  if (item.mets_crust_cd) parts.push(`生地${item.mets_crust_cd}`);
  if (item.mets_size_cd) parts.push(`サイズ${item.mets_size_cd}`);
  return parts.length > 0 ? (
    <Text as="span" color="gray.500" fontFamily="mono" fontSize="11px">
      {parts.join(' · ')}
    </Text>
  ) : (
    muted()
  );
};

const ChildTableHead: React.FC = () => (
  <Thead
    style={{
      backgroundColor: '#F5F8FB',
      borderBottom: '1px solid #E2E8F0',
      height: '48px',
    }}
    data-testid="relation-detail-table-head"
  >
    <Tr>
      <Th w="92px">種別</Th>
      <Th w="84px">子コード</Th>
      <Th>子商品名</Th>
      <Th w="80px" isNumeric>
        数量
      </Th>
      <Th w="80px" isNumeric>
        表示順
      </Th>
      <Th w="150px">単価（通常/軽減）</Th>
      <Th w="170px">価格差額（通常/軽減）</Th>
      <Th w="172px" whiteSpace="nowrap">
        価格の適用条件
      </Th>
    </Tr>
  </Thead>
);

const RelationTable: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Box data-testid="relation-detail-table" overflowX="auto">
    <Table size="sm" style={{ minWidth: '860px' }}>
      <ChildTableHead />
      <Tbody>{children}</Tbody>
    </Table>
  </Box>
);

/** グループ見出し。左にタグ、続けてタイトルと補足。 */
const GroupHead: React.FC<{
  tag: string;
  kind: RelationKind;
  tagTestId: string;
  title: string;
  sub: React.ReactNode;
  testId?: string;
  style?: React.CSSProperties;
}> = ({
  tag,
  kind,
  tagTestId,
  title,
  sub,
  testId = 'relation-group-head',
  style,
}) => (
  <Flex
    align="center"
    data-testid={testId}
    wrap="wrap"
    style={{ ...relationGroupHeadStyle, ...style }}
  >
    <Badge data-testid={tagTestId} style={kindBadgeStyle(kind)}>
      {tag}
    </Badge>
    <Text fontSize="xs" fontWeight="bold">
      {title}
    </Text>
    <Text fontSize="xs" color="gray.500">
      {sub}
    </Text>
  </Flex>
);

const ChildRow: React.FC<{
  item: ChildItem;
  label: string;
  kind: RelationKind;
  showDifference?: boolean;
  businessDate: string;
}> = ({ item, label, kind, showDifference = false, businessDate }) => (
  <Tr data-testid={`relation-row-${item.mets_menu_cd}`}>
    <Td>
      <Badge style={kindBadgeStyle(kind)}>{label}</Badge>
    </Td>
    <Td fontFamily="mono">{item.mets_menu_cd}</Td>
    <Td>{item.mets_screen_menu_name}</Td>
    <Td isNumeric>×{item.quantity}</Td>
    <Td isNumeric>{item.display_order}</Td>
    <Td data-testid={`relation-price-${item.mets_menu_cd}`}>
      {priceText(item.prices, businessDate)}
    </Td>
    <Td data-testid={`relation-diff-${item.mets_menu_cd}`}>
      {showDifference ? priceDifference(item) : muted()}
    </Td>
    <Td data-testid={`relation-condition-${item.mets_menu_cd}`}>
      {priceCondition(item)}
    </Td>
  </Tr>
);

const sortByDisplayOrder = <T extends ChildItem>(items: T[]): T[] =>
  [...items].sort(
    (left, right) =>
      left.display_order - right.display_order ||
      left.mets_menu_cd.localeCompare(right.mets_menu_cd)
  );

const taxRateLabel = (flag: number): string =>
  flag === 1 ? '軽減税率対象' : '軽減税率対象外';

/**
 * root 商品の子商品（セット構成・チョイス・付属品）の展開表示です。
 * SCR009 No.15 と SCR010 No.16 は同一パターンと定義されているため、
 * 両画面で同じコンポーネントを使います。
 */
export const MenuRelationDetail: React.FC<Props> = ({ menu, businessDate }) => {
  const isEmpty =
    menu.set_configs.length === 0 && menu.accessories.length === 0;

  return (
    <Box>
      {menu.set_configs.map((setConfig) => {
        const parentPrice =
          priceAt(setConfig.prices, businessDate) ??
          priceAt(menu.prices, businessDate);
        const included = sortByDisplayOrder(setConfig.included_items);

        return (
          <Box
            key={setConfig.set_parent_mets_menu_cd}
            mb={3}
            data-testid={`relation-group-set-${setConfig.set_parent_mets_menu_cd}`}
            style={relationGroupStyle}
          >
            <GroupHead
              tag="セット構成"
              kind="included"
              tagTestId={`relation-tag-set-${setConfig.set_parent_mets_menu_cd}`}
              title={setConfig.set_parent_name}
              sub={
                <>
                  セット商品コード {setConfig.set_parent_mets_menu_cd}
                  {parentPrice ? ` · ${formatYen(parentPrice.sell_price)}` : ''}
                  {' · '}
                  {taxRateLabel(setConfig.set_parent_reduced_tax_rate_flag)}
                </>
              }
            />
            <RelationTable>
              {included.length > 0 ? (
                included.map((item) => (
                  <ChildRow
                    key={item.mets_menu_cd}
                    item={item}
                    label="構成品"
                    kind="included"
                    businessDate={businessDate}
                  />
                ))
              ) : (
                <Tr>
                  <Td colSpan={8}>{muted('必ず含まれる構成品はありません')}</Td>
                </Tr>
              )}
            </RelationTable>

            {setConfig.choice_groups.map((group) => (
              <Box
                key={group.mets_choice_cd}
                data-testid={`relation-group-choice-${group.mets_choice_cd}`}
              >
                <GroupHead
                  tag="チョイス"
                  kind="choice"
                  tagTestId={`relation-tag-choice-${group.mets_choice_cd}`}
                  title={group.choice_group_name || '（チョイス名なし）'}
                  sub={
                    <>
                      チョイスコード {group.mets_choice_cd} · この中から1つ選択
                    </>
                  }
                  style={{ borderTop: '1px solid #E2E8F0' }}
                />
                <RelationTable>
                  {sortByDisplayOrder(group.selectable_items).map((item) => (
                    <ChildRow
                      key={item.mets_menu_cd}
                      item={item}
                      label="チョイス"
                      kind="choice"
                      showDifference
                      businessDate={businessDate}
                    />
                  ))}
                </RelationTable>
              </Box>
            ))}
          </Box>
        );
      })}

      {menu.accessories.length > 0 && (
        <Box
          mb={3}
          data-testid="relation-group-accessory"
          style={relationGroupStyle}
        >
          <GroupHead
            tag="付属品"
            kind="accessory"
            tagTestId="relation-tag-accessory"
            title="付属品"
            sub="親商品に自動で付きます · 明細上の価格は 0 円"
          />
          <RelationTable>
            {sortByDisplayOrder(menu.accessories as MosPosAccessory[]).map(
              (accessory) => (
                <ChildRow
                  key={accessory.mets_menu_cd}
                  item={accessory}
                  label="付属"
                  kind="accessory"
                  businessDate={businessDate}
                />
              )
            )}
          </RelationTable>
        </Box>
      )}

      {isEmpty && (
        <Text fontSize="xs" color="gray.500">
          子商品はありません。
        </Text>
      )}
    </Box>
  );
};
